import '@dni-types/vite-env'
import { type AppEnv } from '@dni-types/vite-env'

type PublicEnvKeys =
  | 'NODE_ENV'
  | 'APP_ENV'
  | 'ALGOLIA_SUGGESTIONS_INDEX'
  | 'ALGOLIA_APP_KEY'
  | 'ALGOLIA_APP_ID'
  | 'X_PLATFORM_TOKEN'
  | 'E_COMMERCE_API_URL'
  | 'FORTUNE_API_URL'
  | 'FORTUNE_API_TOKEN'
  | 'WP_API_URL'
  | 'RELAY_PATH_BASE_URL'
  | 'COSIGN_API_URL'
  | 'LINE_LIFF_ID'
  | 'LINE_LIFF_ID_FOR_GAMES'
  | 'GA4_TRACKING_ID'
  | 'GTM_CONTAINER_ID'
  | 'GTM_SERVING_PATH'
  | 'META_TRACKING_ID'
  | 'TIKTOK_TRACKING_ID'
  | 'OMNI_SEGMENT_CDP_TRACKING_ID'
  | 'OMNICHAT_APP_KEY'
  | 'GOOGLE_ADS_CONVERSION_ID_TW'
  | 'GOOGLE_ADS_CONVERSION_ID_HK'
  | 'STATSIG_SDK_KEY'
  | 'UDN_GTM_CONTAINER_ID'
  | 'UDN_GA4_TRACKING_ID'
  | 'BASE_URL'
  | 'GUEST_PURCHASE_COUNTRIES'

const getViteEnvVar = (name: PublicEnvKeys): string | undefined => {
  const envs: Record<PublicEnvKeys, string | undefined> = {
    NODE_ENV: process.env.NODE_ENV,
    APP_ENV: import.meta.env.VITE_APP_ENV,
    ALGOLIA_SUGGESTIONS_INDEX: import.meta.env.VITE_ALGOLIA_SUGGESTIONS_INDEX,
    ALGOLIA_APP_KEY: import.meta.env.VITE_ALGOLIA_APP_KEY,
    ALGOLIA_APP_ID: import.meta.env.VITE_ALGOLIA_APP_ID,
    X_PLATFORM_TOKEN: import.meta.env.VITE_X_PLATFORM_TOKEN,
    E_COMMERCE_API_URL: import.meta.env.VITE_E_COMMERCE_API_URL,
    FORTUNE_API_URL: import.meta.env.VITE_FORTUNE_API_URL,
    FORTUNE_API_TOKEN: import.meta.env.VITE_FORTUNE_API_TOKEN,
    WP_API_URL: import.meta.env.VITE_WP_API_URL,
    RELAY_PATH_BASE_URL: import.meta.env.VITE_RELAY_PATH_BASE_URL,
    COSIGN_API_URL: import.meta.env.VITE_COSIGN_API_URL,
    LINE_LIFF_ID: import.meta.env.VITE_LINE_LIFF_ID,
    LINE_LIFF_ID_FOR_GAMES: import.meta.env.VITE_LINE_LIFF_ID_FOR_GAMES,
    GA4_TRACKING_ID: import.meta.env.VITE_GA4_TRACKING_ID,
    GTM_CONTAINER_ID: import.meta.env.VITE_GTM_CONTAINER_ID,
    GTM_SERVING_PATH: import.meta.env.VITE_GTM_SERVING_PATH,
    META_TRACKING_ID: import.meta.env.VITE_META_TRACKING_ID,
    TIKTOK_TRACKING_ID: import.meta.env.VITE_TIKTOK_TRACKING_ID,
    OMNI_SEGMENT_CDP_TRACKING_ID: import.meta.env.VITE_OMNI_SEGMENT_CDP_TRACKING_ID,
    OMNICHAT_APP_KEY: import.meta.env.VITE_OMNICHAT_APP_KEY,
    GOOGLE_ADS_CONVERSION_ID_TW: import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID_TW,
    GOOGLE_ADS_CONVERSION_ID_HK: import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID_HK,
    STATSIG_SDK_KEY: import.meta.env.VITE_STATSIG_SDK_KEY,
    UDN_GTM_CONTAINER_ID: import.meta.env.VITE_UDN_GTM_CONTAINER_ID,
    UDN_GA4_TRACKING_ID: import.meta.env.VITE_UDN_GA4_TRACKING_ID,
    BASE_URL: import.meta.env.VITE_BASE_URL,
    GUEST_PURCHASE_COUNTRIES: import.meta.env.VITE_GUEST_PURCHASE_COUNTRIES,
  }
  return envs[name]
}

/**
 * Note: Next.js env not support dynamic inline
 * https://nextjs.org/docs/pages/guides/environment-variables
 */
const getNextEnvVar = (name: PublicEnvKeys): string | undefined => {
  const envs: Record<PublicEnvKeys, string | undefined> = {
    NODE_ENV: process.env.NODE_ENV,
    APP_ENV: process.env.NEXT_PUBLIC_APP_ENV as AppEnv,
    ALGOLIA_SUGGESTIONS_INDEX: process.env.NEXT_PUBLIC_ALGOLIA_SUGGESTIONS_INDEX,
    ALGOLIA_APP_KEY: process.env.NEXT_PUBLIC_ALGOLIA_APP_KEY,
    ALGOLIA_APP_ID: process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
    X_PLATFORM_TOKEN: process.env.NEXT_PUBLIC_X_PLATFORM_TOKEN,
    E_COMMERCE_API_URL: process.env.NEXT_PUBLIC_E_COMMERCE_API_URL,
    FORTUNE_API_URL: process.env.NEXT_PUBLIC_FORTUNE_API_URL,
    FORTUNE_API_TOKEN: process.env.NEXT_PUBLIC_FORTUNE_API_TOKEN,
    WP_API_URL: process.env.NEXT_PUBLIC_WP_API_URL,
    RELAY_PATH_BASE_URL: process.env.NEXT_PUBLIC_RELAY_PATH_BASE_URL,
    COSIGN_API_URL: process.env.NEXT_PUBLIC_COSIGN_API_URL,
    LINE_LIFF_ID: process.env.NEXT_PUBLIC_LINE_LIFF_ID,
    LINE_LIFF_ID_FOR_GAMES: process.env.NEXT_PUBLIC_LINE_LIFF_ID_FOR_GAMES,
    GA4_TRACKING_ID: process.env.NEXT_PUBLIC_GA4_TRACKING_ID,
    GTM_CONTAINER_ID: process.env.NEXT_PUBLIC_GTM_CONTAINER_ID,
    GTM_SERVING_PATH: process.env.NEXT_PUBLIC_GTM_SERVING_PATH,
    META_TRACKING_ID: process.env.NEXT_PUBLIC_META_TRACKING_ID,
    TIKTOK_TRACKING_ID: process.env.NEXT_PUBLIC_TIKTOK_TRACKING_ID,
    OMNI_SEGMENT_CDP_TRACKING_ID: process.env.NEXT_PUBLIC_OMNI_SEGMENT_CDP_TRACKING_ID,
    OMNICHAT_APP_KEY: process.env.NEXT_PUBLIC_OMNICHAT_APP_KEY,
    GOOGLE_ADS_CONVERSION_ID_TW: process.env.NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_ID_TW,
    GOOGLE_ADS_CONVERSION_ID_HK: process.env.NEXT_PUBLIC_GOOGLE_ADS_CONVERSION_ID_HK,
    STATSIG_SDK_KEY: process.env.NEXT_PUBLIC_STATSIG_SDK_KEY,
    UDN_GTM_CONTAINER_ID: process.env.NEXT_PUBLIC_UDN_GTM_CONTAINER_ID,
    UDN_GA4_TRACKING_ID: process.env.NEXT_PUBLIC_UDN_GA4_TRACKING_ID,
    BASE_URL: process.env.NEXT_PUBLIC_BASE_URL,
    GUEST_PURCHASE_COUNTRIES: process.env.NEXT_PUBLIC_GUEST_PURCHASE_COUNTRIES,
  }
  return envs[name]
}

/**
 * Support Next.js and Vite
 * Only for public env var (start with NEXT_PUBLIC_ or VITE_)
 */
export const getPublicEnvVar = (name: PublicEnvKeys): string | undefined => {
  try {
    const isVite = typeof import.meta.env !== 'undefined'
    return isVite ? getViteEnvVar(name) : getNextEnvVar(name)
  }
  catch (error) {
    console.error(`Failed to get environment variable '${name}':`, error)
    return undefined
  }
}
